import type {ReactElement} from 'react';
import React from 'react';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import type {RGBA} from '@Utils/color.util';
import {rgbaToHexString} from '@Utils/color.util';
import {getDefaultFabricShadowForType, getFabricShadowOrGlowTypeFromShadowType, isCustomShadow, isShadow} from '@Utils/fabric-shadow.util';
import {convertShadowOptionsBlurToFabricShadowBlur} from '@Utils/fabric-shadow.util';
import {getFabricShadowOffsetsFromShadowOptions} from '@Utils/fabric-shadow.util';
import type {ShadowType} from '@PosterWhiteboard/poster/poster-backend.types';
import {useGetShadowOptionsObjectOfActiveSlideItem} from '@Hooks/poster-editor/useGetShadowOptionsObjectOfActiveSlideItem';
import {BaseItemShadowOptions} from '@Components/poster-editor/components/base-item-shadow-options/base-item-shadow-options';

interface SlideshowMobileShadowOptionsProps {
  panelId: string;
  onClickColorOption(): void;
}

export function SlideshowMobileShadowOptions({...props}: SlideshowMobileShadowOptionsProps): ReactElement | null {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const activeSlideShadowOptions = useGetShadowOptionsObjectOfActiveSlideItem();

  if (!activeItem?.isSlideshow()) {
    return null;
  }

  if (!activeSlide || !activeSlideUID) {
    return null;
  }

  if (activeSlideShadowOptions === undefined) {
    return null;
  }

  const updateDistance = (dropShadowDistance: number, angle: number, undoable = true): void => {
    if (!activeSlideShadowOptions) {
      return;
    }

    void activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        shadow: {
          ...getFabricShadowOffsetsFromShadowOptions({...activeSlideShadowOptions, distance: dropShadowDistance, angle}, activeSlide.getScaleForShadow()),
        },
      },
      {
        undoable,
      }
    );
  };

  const updateBlur = (dropShadowBlur: number, undoable = true): void => {
    if (!activeSlideShadowOptions) {
      return;
    }

    void activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        shadow: {
          blur: convertShadowOptionsBlurToFabricShadowBlur(dropShadowBlur, activeSlide.getScaleForShadow()),
        },
      },
      {
        undoable,
      }
    );
  };

  const updateOpacity = (dropShadowAlpha: number, undoable = true): void => {
    if (!activeSlideShadowOptions) {
      return;
    }

    const activeSlideDropShadowColor = activeSlideShadowOptions.color.slice() as RGBA;
    activeSlideDropShadowColor[3] = dropShadowAlpha;
    void activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        shadow: {
          color: rgbaToHexString(activeSlideDropShadowColor),
        },
      },
      {
        undoable,
      }
    );
  };

  const updateAngle = (dropShadowAngle: number, undoable = true): void => {
    if (!activeSlideShadowOptions) {
      return;
    }

    void activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        shadow: {
          ...getFabricShadowOffsetsFromShadowOptions({...activeSlideShadowOptions, angle: dropShadowAngle}, activeItem.getScaleForShadow()),
        },
      },
      {
        undoable,
      }
    );
  };

  const onToggleShadow = (newShadowType: ShadowType | null): void => {
    const newType = getFabricShadowOrGlowTypeFromShadowType(newShadowType);
    const currentType = activeSlideShadowOptions?.shadowOrGlowType;

    const updatedShadow = getDefaultFabricShadowForType(newType, activeSlide.gitype, activeSlide.getScaleForShadow());
    if (!updatedShadow) {
      void activeItem.updateSlideFromObject(activeSlideUID, {shadow: null});

      return;
    }

    const shouldPreserveOffsets = isShadow(currentType) && isShadow(newType) && !isCustomShadow(currentType) && !isCustomShadow(newType);

    const {offsetX, offsetY, ...restShadow} = updatedShadow;

    const shadowToApply = shouldPreserveOffsets ? {...restShadow} : updatedShadow;

    void activeItem.updateSlideFromObject(activeSlideUID, {shadow: shadowToApply});
  };

  const getShadowOptions = (): ReactElement => {
    return (
      <BaseItemShadowOptions
        isMobile
        panelId={props.panelId}
        shadowOptions={activeSlideShadowOptions}
        itemType={activeSlide.gitype}
        onClickColorFill={(): void => {
          props.onClickColorOption();
        }}
        onToggleShadow={onToggleShadow}
        onUpdateOpacity={updateOpacity}
        onUpdateAngle={updateAngle}
        onUpdateDistance={updateDistance}
        onUpdateBlur={updateBlur}
      />
    );
  };

  return getShadowOptions();
}
